import _ from 'lodash'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '../../Button'
import { Dropdown } from '../../Dropdown'
import { Icon } from '../../Icons'
import { Image } from '../../Image'
import { Menu, MenuItem } from '../../Menu'
import { ColumnType } from '../Table'

export interface SelectableColumn {
  id: string
  title: string
}

export interface ColumnVisibilitySelectorProps<T> {
  columns: ColumnType<T>[]
  visibleColumns?: string[]
  onSelectionChange(col: string[]): void
}

function toggleColumnSelected<T>(columns: ColumnType<T>[], column: string, visibleColumns?: string[]) {
  const col = _.find(columns, c => c.id === column)
  columns = _.reject(columns, col => _.includes(['non-selectable'], col.visibilityType))

  if (_.isEmpty(visibleColumns)) {
    if (col?.defaultHidden) {
      return _.concat(
        _.map(
          _.filter(columns, x => !x.defaultHidden),
          'id'
        ),
        [column]
      )
    } else {
      return _.difference(
        _.map(
          _.filter(columns, x => !x.defaultHidden),
          'id'
        ),
        [column]
      )
    }
  }
  return _.xor(visibleColumns, [column])
}

export const ColumnVisibilitySelector = <T extends object>({
  columns,
  visibleColumns,
  onSelectionChange
}: ColumnVisibilitySelectorProps<T>) => {
  const [menuVisible, setMenuVisible] = useState(false)
  const { t } = useTranslation('form')

  columns = _.reject(columns, x => x.visibilityType === 'non-selectable')

  const menu = (
    <Menu variant="primary">
      {_.map(columns, col => (
        <MenuItem
          disabled={col.visibilityType === 'fixed'}
          icon={
            col.visibilityType === 'fixed' ||
            (_.isEmpty(visibleColumns) && !col.defaultHidden) ||
            _.includes(visibleColumns, col.id) ? (
              <Icon type="CheckSquareOutlined" color="white" />
            ) : (
              <Icon type="BorderOutlined" color="white" />
            )
          }
          variant="primary"
          key={col.id}
          onClick={() =>
            col.visibilityType !== 'fixed' && onSelectionChange(toggleColumnSelected(columns, col.id, visibleColumns))
          }
        >
          {col.title}
        </MenuItem>
      ))}
    </Menu>
  )

  return (
    <Dropdown open={menuVisible} onOpenChange={setMenuVisible} dropdownRender={() => menu}>
      <Button variant="primary" size="small" onClick={() => setMenuVisible(!menuVisible)}>
        {t('form:SelectColumns')} <Image alt="down" type="down" />
      </Button>
    </Dropdown>
  )
}
